import React from 'react';
import type {ISelectOption} from 'src/types/Form';

/**
 * Configures which field-type/settings-panel palette a `FormBuilderContent`
 * instance renders. Lets the same builder tree be reused by both the Forms
 * module (full palette + Form Settings) and other embedders (e.g. InSpec's
 * field editor) that need a narrower field-type list, a different settings
 * panel set, and no form-level settings accordion.
 */
export interface FormBuilderRegistry {
  /** Options rendered in the field type <Select> and used for the default add-field type. */
  fieldTypes: ISelectOption[];
  /**
   * Ordered field-settings panels. Each panel self-filters on the selected
   * field's `type` (the existing pattern — see `DropdownSettings`).
   */
  settingsPanels: React.ComponentType[];
  /** Whether `FormBuilderContent` renders the Form Settings accordion. */
  showFormSettings: boolean;
  /**
   * Whether `FormPreview` injects the static "Form Description" identifier
   * field when `enableFormIdentifierField` is on. Defaults to true; embedders
   * without form-level settings (e.g. InSpec) set false so the default-on
   * flag doesn't leak an identifier field they can never toggle off.
   */
  showIdentifierFieldInPreview?: boolean;
  /**
   * Replaces `FormPreview`'s hardcoded `FormContentHeader`/"New Form
   * Title"/"[Form ID]" block when provided. Omit to keep the default Forms
   * behavior.
   */
  previewHeader?: React.ComponentType;
  /** Rendered above the field list in `FormPreview` when provided. */
  previewLeadIn?: React.ComponentType;
}

const FormBuilderRegistryContext =
  React.createContext<FormBuilderRegistry | null>(null);

export interface FormBuilderRegistryProviderProps {
  registry: FormBuilderRegistry;
  children: React.ReactNode;
}

export const FormBuilderRegistryProvider: React.FC<
  FormBuilderRegistryProviderProps
> = ({registry, children}) => (
  <FormBuilderRegistryContext.Provider value={registry}>
    {children}
  </FormBuilderRegistryContext.Provider>
);

export const useFormBuilderRegistry = (): FormBuilderRegistry => {
  const registry = React.useContext(FormBuilderRegistryContext);
  if (!registry) {
    throw new Error(
      'useFormBuilderRegistry must be used within a FormBuilderRegistryProvider'
    );
  }
  return registry;
};
